import { useEffect, useMemo, type FC } from "react";
import { useSelector } from "react-redux";

import { useDynamicBonusCatalog } from "../../../../queries/dynamicBonuses/useDynamicBonusCatalog";
import type { RootState } from "../../../../store";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { Input, Select } from "../../../shared";
import { getClientCurrencyOptions } from "../clientCurrencies";

export type ClientCurrencyFieldProps = {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  provider?: string;
  "data-testid"?: string;
};

/**
 * Free-spin currency from this provider's currency_code_map keys.
 * One currency: locked. Multiple: dropdown defaulting to cash (first key).
 * Empty / missing maps are unknown — never rewrite a stored value with a guess.
 */
const ClientCurrencyField: FC<ClientCurrencyFieldProps> = ({
  value,
  onChange,
  disabled = false,
  provider,
  ...rest
}) => {
  const testId = rest["data-testid"] ?? "client-currency-field";
  const appCurrencyConfig = useSelector(
    (state: RootState) => state.config.appConfig?.app_currency_config,
  );
  const pamAppName = AppStrings.PAM_APP_NAME;
  const primaryCurrency = AppStrings.PRIMARY_CURRENCY;
  const { data: catalog, isError, isLoading } = useDynamicBonusCatalog();
  const providerKey = provider?.trim() ?? "";
  const current = typeof value === "string" ? value.trim() : "";
  const mapKeys = useMemo(() => {
    if (!providerKey) return [];
    const raw = catalog?.free_spin_currencies?.[providerKey];
    if (!Array.isArray(raw)) return [];
    return raw.map((key) => String(key).trim()).filter(Boolean);
  }, [catalog?.free_spin_currencies, providerKey]);
  const catalogPending = Boolean(providerKey) && isLoading && !catalog && !isError;
  const hasAuthoritativeMap = mapKeys.length > 0;
  const options = useMemo(() => {
    if (hasAuthoritativeMap) {
      return getClientCurrencyOptions(appCurrencyConfig, {
        pamAppName,
        primaryCurrency,
        mapKeys,
      });
    }
    // Unknown map: surface the stored key as-is so edit does not invent a fallback.
    if (current) {
      return [{ value: current, label: current }];
    }
    return [];
  }, [
    appCurrencyConfig,
    current,
    hasAuthoritativeMap,
    mapKeys,
    pamAppName,
    primaryCurrency,
  ]);
  const defaultValue = options[0]?.value;
  const locked = options.length <= 1;
  const allowed = useMemo(
    () => new Set(options.map((option) => option.value)),
    [options],
  );

  useEffect(() => {
    if (catalogPending || !hasAuthoritativeMap || !defaultValue) return;
    if (!current || !allowed.has(current)) {
      onChange?.(defaultValue);
    }
  }, [allowed, catalogPending, current, defaultValue, hasAuthoritativeMap, onChange]);

  const emptyHint = catalogPending
    ? AppStrings.PAM_DYNAMIC_BONUS_CURRENCY_LOADING
    : providerKey
      ? AppStrings.PAM_DYNAMIC_BONUS_CURRENCY_UNAVAILABLE
      : AppStrings.PAM_DYNAMIC_BONUS_CURRENCY_SELECT_PROVIDER;

  if (locked) {
    return (
      <Input
        data-testid={testId}
        value={options[0]?.label ?? defaultValue ?? ""}
        placeholder={options.length === 0 ? emptyHint : undefined}
        disabled
        readOnly
      />
    );
  }

  return (
    <Select
      data-testid={testId}
      value={value || defaultValue}
      options={options}
      onChange={(next) => onChange?.(String(next))}
      disabled={disabled}
      allowClear={false}
    />
  );
};

export default ClientCurrencyField;
